import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import { Spiner } from "../../components/spinner/Spiner";
import Button from "../../components/button/Button";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage, PointTypeId } from "../../config/const";
import pointDataQuery from "../../firestore/PointData.query";
import { PointAwardedEditModal } from "./PointAwardedEditModal";
import { PointCategoryEditModal } from "./PointCategoryEditModal";
import { auth } from "../../firebase";
import { PointCategory, PtCategorySettings, PtAwardedSettings, newPtCategorySettings, PtBonusSettings, RangeStepsMap } from "../../model/PointData";
import { checkOption } from "../../controller/OptionController";
import { compareDateOnlyYmd } from "../../utility/CommonUtility";

export const PointAwardedList = () => {

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;

  const navigation = useNavigate();
  const dispatch = useDispatch();

  const [isLoad, setIsLoad] = useState(true);
  const [isOpenSettingsDialog, setIsOpenSettingsDialog] = useState(false);
  const [isOpenAddDialog, setIsOpenAddDialog] = useState(false);
  const [pointCategoryList, setPointCategoryList] = useState<PointCategory[]>([]);
  const [ptCategorySettingsList, setPtCategorySettingsList] = useState<PtCategorySettings[]>([]);
  const [ptAwardedSettingsList, setPtAwardedSettingsList] = useState<PtAwardedSettings[]>([]);
  const [ptCategorySettingInitial, setPtCategorySettingInitial] = useState<PtCategorySettings>(newPtCategorySettings);
  const [pointCategorySelected, setPointCategorySelected] = useState(0);
  const bonusPointMap = new Map<string, number|undefined>();
  const [bonusPointList, setBonusPointList] = useState< PtBonusSettings[][]>([]);

  useEffect(() => {
    const f = async () => {
      try {
        // pointCategory取得
        const pointCategoryList = await pointDataQuery.getPointCategoryList();
        setPointCategoryList(pointCategoryList);

        // ptCategorySettings取得
        const ptCategorySettingsList = await pointDataQuery.getPtCategorySettingsList(currentUser?.tenantId!);
        setPtCategorySettingsList(ptCategorySettingsList);

        const pointCategory = pointCategoryList[0];
        const ptCategorySetting = ptCategorySettingsList.filter((list) => list.categoryId === pointCategory.categoryId);
        const ptCategorySettingInitialData = ptCategorySetting.length > 0 ? ptCategorySetting[0] : newPtCategorySettings;
        setPtCategorySettingInitial(ptCategorySettingInitialData);

        // ptAwardedSettings取得
        const ptAwardedSettingsList = await pointDataQuery.getPtAwardedSettings(currentUser?.tenantId!);
        setPtAwardedSettingsList(ptAwardedSettingsList);
        
        //ポイント種別がボーナスの項目のボーナスポイントを取得
        const kindBonusList = ptAwardedSettingsList.filter((list) => list.pointKind == 1)
        let bonusPointList = [];
        for (let i = 0; i < kindBonusList.length; i++) {
          const list = await pointDataQuery.getPtBonusSettings(currentUser?.tenantId!, kindBonusList[i].id);
          bonusPointList.push(list);
        }
        setBonusPointList(bonusPointList);

      } catch (error) {
        console.error(error);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
      }
      setIsLoad(false);
    };
    f();
  },[dispatch, isOpenAddDialog, navigator]);

  const statusName = (status: number, validPeriodDateFrom: Date | undefined, validPeriodDateTo: Date | undefined) => {
    if (status === 0) {
      // ステータス：無効
      return "無効"
    } else {
      // ステータス：有効
      const now = new Date();
      const nowCompFrom = compareDateOnlyYmd(now, validPeriodDateFrom);
      if (!validPeriodDateTo) {
        // 終了年月日なし
        if (nowCompFrom === 1) {
          // 開始前
          return "開始待ち";
        } else {
          // 開始後
          return "有効";
        }
      } else {
        // 終了年月日あり
        const nowCompTo = compareDateOnlyYmd(now, validPeriodDateTo);
        if (nowCompFrom! <= 0 && 0 <= nowCompTo!) {
          // 開始・終了範囲内
          return "有効";
        } else {
          // 開始・終了範囲外
          return "無効";
        }
      }
    }
  }

  const ptKindName = (kind: number) => {
    if(kind)
    {
      return "ボーナス";
    } else {
      return "通常";
    }
  }


  const columns: GridCol[] = [
    {
      field: "name",
      headerName: "ポイント名",
      style: "w-6/20",
      sort: false
    },
    {
      field: "status",
      headerName: "ステータス",
      style: "w-1/20",
      sort: false
    },
    {
      field: "pointKind",
      headerName: "ポイント種別",
      style: "w-1/20",
      sort: false
    },
    {
      field: "point",
      headerName: "ポイント数",
      style: "w-2/20",
      sort: false
    },
    {
      field: "bonusSetting",
      headerName: "ボーナス設定",
      style: "w-1/20",
      sort: false
    },
    {
      field: "categoryName",
      headerName: "カテゴリ",
      style: "w-4/20",
      sort: false
    },
    {
      field: "typeName",
      headerName: "タイプ",
      style: "w-4/20",
      sort: false
    },
    {
      field: "info",
      headerName: "",
      style: "w-1/20",
      sort: false
    },
  ];

  const rowList: GridRow[] = ptAwardedSettingsList.map<GridRow>((item) => {
    let displayPoint = item.basePoint;
    let pointTitle = "";
    let secPointTitle = "";
    let secPoint = undefined;
    let thirdPointTitle = "";
    let thirdPoint = undefined;
    switch (item.typeId) {
      case PointTypeId.STEPS_SPECIFIED :
      case PointTypeId.STEPS_TARGET :
      case PointTypeId.STEPS_RANGE :
        // ポイント種別がボーナスのとき
        if (item.pointKind === 1) {
          displayPoint = undefined;
          for (let i = 0; i < bonusPointList.length; i++) {
            //ボーナスポイント設定が複数あるとき
            if (bonusPointList[i].length > 1) {
              bonusPointMap.set(bonusPointList[i][0].ptAwardedId, undefined);
            } else {
              bonusPointMap.set(bonusPointList[i][0].ptAwardedId, bonusPointList[i][0].bonusPoints);
            }
          }
        } else if (item.typeId == PointTypeId.STEPS_RANGE) {
          if (item.rangeStepsMap) {
            const rangeStepsMap: [string, RangeStepsMap][] = Object.entries(item.rangeStepsMap)
            if (rangeStepsMap.length == 1) {
              displayPoint = Number(item.rangeStepsMap[0].basePoint);
            } else {
              displayPoint = undefined;
            }
          }
        } else {
          displayPoint = item.basePoint;
        }
        break;
      case PointTypeId.VITAL_REGIST:
        if (item.pointSubject === 2) {
          pointTitle = "体重登録：";
          displayPoint = item.registWtPoint ?? 0;
          secPointTitle = "血圧登録：";
          secPoint = item.registBpPoint ?? 0;
          if(item.registSamePoint){
            thirdPointTitle = "同時登録：";
            thirdPoint = item.registSamePoint;
          }
        } else if (item.pointSubject === 0) {
          displayPoint = item.registWtPoint;
        } else if (item.pointSubject === 1) {
          displayPoint = item.registBpPoint;
        }
        break;
      default :
        displayPoint = item.basePoint;
    }

    return{
      rowData: {
        name: item.name,
        status: statusName(item.status!, item.validPeriodDateFrom, item.validPeriodDateTo),
        point: (<div className={"w-[7rem] whitespace-break-spaces"}>
          <div className = {"flex items-center"} >
            <div className={`${displayPoint ? "w-[4rem]" : ""}`}>
              {pointTitle}
            </div>
            <div className={`${displayPoint ? ("w-[3rem] text-right") : "w-[7rem] text-right"}`}>
              {displayPoint ? displayPoint : ((bonusPointMap.get(item.id) != undefined) ? (bonusPointMap.get(item.id)) : "複数設定あり")}
            </div>
          </div>
          {secPointTitle != undefined && secPointTitle != "" ? (
            <>
              <div className = {"flex items-center"} >
                <div className={"w-[4rem]"}>
                  {"\n" + secPointTitle}
                </div>
                <div className = {"w-[3rem] text-right"}>
                  {"\n" + secPoint}
                </div>
              </div>
            </> 
          ) :
          (<></>)}
          {thirdPointTitle != undefined && thirdPointTitle != "" ? (
            <>
              <div className = {"flex items-center"} >
                <div className={"w-[4rem]"}>
                  {"\n" + thirdPointTitle}
                </div>
                <div className = {"w-[3rem] text-right"}>
                  {"\n" + thirdPoint}
                </div>
              </div>
            </> 
          ) :
          (<></>)}
        </div>),
        pointKind: ptKindName(item.pointKind),
        bonusSetting: item.bonusSettingFlg ? "あり" : "なし",
        categoryName: item.categoryName,
        typeName: item.typeName,
        info: (<Button className={"sm:px-4 whitespace-nowrap"} onClick={()=> navigation(`${item.id}/info`)}>詳細</Button>),
      }
    };
  });

  // カテゴリ設定モーダルの開閉トリガ
  const toggleSettingsDialog = () => {
    setIsOpenSettingsDialog(!isOpenSettingsDialog);
  };

  const onClickCategorySettingsSubmit = async (data: PtCategorySettings & {expirationYmString: string}) => {
    try {
      if (data.limitPointFlg === true) {
        if (data.numberOfGrantsMap?.unit === 'year') {
          data.numberOfGrantsMap = {
            unit: data.numberOfGrantsMap.unit,
            limitPoint: Number(data.numberOfGrantsMap.limitPoint),
            baseMonth: data.numberOfGrantsMap.baseMonth,
          }
        } else {
          data.numberOfGrantsMap = {
            unit: data.numberOfGrantsMap?.unit,
            limitPoint: data.numberOfGrantsMap?.limitPoint,
          }
        }
      } else {
        if (Object.keys(data).includes('numberOfGrantsMap') === true) {
          delete data.numberOfGrantsMap;
        }
      }
      if (data.expireFlg === true) {
        if (data.dateOfExpiryMap?.expirationUnit === 'limitMonth') {
          data.dateOfExpiryMap = {
            expirationUnit: data.dateOfExpiryMap.expirationUnit,
            expirationYm: new Date(data.expirationYmString),
          }
        } else {
          data.dateOfExpiryMap = {
            expirationUnit: data.dateOfExpiryMap?.expirationUnit,
            expirationValue: Number(data.dateOfExpiryMap?.expirationValue),
          }
        }
      } else {
        if (Object.keys(data).includes('dateOfExpiryMap') === true) {
          delete data.dateOfExpiryMap;
        }
      }
      if (data.id === "") {
        await pointDataQuery.addPtCategorySettings(currentUser?.tenantId!, data);
      } else {
        await pointDataQuery.updatePtCategorySettings(currentUser?.tenantId!, data);
      }
      const ptCategorySettingsList = await pointDataQuery.getPtCategorySettingsList(currentUser?.tenantId!);
      setPtCategorySettingsList(ptCategorySettingsList);
      dispatch(alertOpen(AlertValue(`カテゴリ設定：${pointCategoryList[pointCategorySelected].categoryName}を登録しました。`, "success")));
    } catch (err: any) {
      console.log(err);
      dispatch(alertOpen(AlertValue("保存に失敗しました。", "danger")));
    }
  }

  // 付与項目追加モーダルの開閉トリガ
  const toggleAddDialog = () => {
    setIsOpenAddDialog(!isOpenAddDialog);
  };

  return (
    <div className="h-full">
      <div className="flex items-center space-x-4 p-6 justify-between">
        <div className="flex items-top sm:flex-auto space-x-4">
          <h2 className="min-w-[120px] text-xl font-semibold text-gray-900">
            ポイント設定一覧
          </h2>
        </div>
        <div className="flex">
          <div>
            <Button
              color={"gray"}
              size={"sm"}
              onClick={async () => {
                if (await checkOption("point", dispatch)) {
                  toggleSettingsDialog();
                  }
                }
              }
            >
            カテゴリ設定
            </Button>
          </div>
          <div className="ml-4">
            <Button
              size={"sm"}
              className={"ml-1"}
              onClick={async () => {
                if (await checkOption("point", dispatch)) {
                  toggleAddDialog();
                  }
                }
              }
            >
            &emsp;設定追加&emsp;
            </Button>
          </div>
        </div>
      </div>
      <div>
        {isOpenSettingsDialog && (
          <PointCategoryEditModal
            children
            open={isOpenSettingsDialog}
            toggle={toggleSettingsDialog}
            closeOnClickOutside={false}
            onClickSubmit={onClickCategorySettingsSubmit}
            pointCategoryList={pointCategoryList}
            ptCategorySettingsList={ptCategorySettingsList}
            ptCategorySettingInitial={ptCategorySettingInitial}
            setPointCategorySelectedToBase={setPointCategorySelected}
          />
        )}
        {isOpenAddDialog && (
          <PointAwardedEditModal
            children
            open={isOpenAddDialog}
            toggle={toggleAddDialog}
            closeOnClickOutside={false}
          />
        )}
        {isLoad === true ? <Spiner/> : (
          <div className={"px-6"}>
            <TableList
              header={false}
              footer={false}
              defaultSortKey={"sort"}
              sort={false}
              gridCol={columns}
              gridRow={rowList}
            />
          </div>
          )
        }
      </div>
    </div>
  );
}